<template>
  <div class="flex flex-wrap items-start gap-4">
    <Select
      class="min-w-[16rem] flex-1"
      :model-value="modelValue"
      :label="label ?? t('taxProcedure')"
      :options="options"
      :hint="reverseChargeHint"
      :is-error="!!reverseChargeHint"
      :disabled="disabled"
      @update:model-value="handleUpdate"
    />
    <LabeledText v-if="countryLabel" :label="t('taxCountry')">
      <span class="inline-flex h-8 items-center">{{ countryLabel }}</span>
    </LabeledText>
  </div>
</template>

<script lang="ts" setup>
import { TaxProcedure } from '@carvia/ros-client-types';
import { useAbility } from '@casl/vue';
import Select from '@/components/Select.vue';
import LabeledText from '@/components/LabeledText.vue';
import { canUseReverseChargeTaxProcedure } from '@/utils/tax-procedure';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = withDefaults(
  defineProps<{
    modelValue: TaxProcedure;
    label?: string;
    vatId?: string | null;
    taxCountry?: string | null;
    disabled?: boolean;
    requireVatIdForReverseCharge?: boolean;
  }>(),
  {
    requireVatIdForReverseCharge: true,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: TaxProcedure): void;
}>();

const { t, locale } = useI18n();
const ability = useAbility();

const countryLabel = computed(() => {
  const code = props.taxCountry?.trim().toUpperCase();
  if (!code || !ability.can('update', 'Booking', 'taxProcedure')) {
    return undefined;
  }
  return new Intl.DisplayNames([locale.value], { type: 'region' }).of(code) ?? code;
});

const canUseReverseCharge = computed(() =>
  canUseReverseChargeTaxProcedure(props.vatId, {
    requireVatId: props.requireVatIdForReverseCharge,
  }),
);

const reverseChargeHint = computed(() =>
  props.modelValue === TaxProcedure.REVERSE_CHARGE && !canUseReverseCharge.value
    ? t('reverseChargeRequiresVatId')
    : undefined,
);

const handleUpdate = (value: string | null | undefined) => {
  if (!value || !Object.values(TaxProcedure).includes(value as TaxProcedure)) {
    return;
  }
  emit('update:modelValue', value as TaxProcedure);
};

const options = computed(() =>
  Object.values(TaxProcedure).map((value) => ({
    value,
    label: t(`taxProcedure.${value}`),
  })),
);
</script>

<i18n lang="json">
{
  "en": {
    "taxProcedure": "Tax procedure",
    "taxProcedure.REGULAR": "Regular",
    "taxProcedure.REVERSE_CHARGE": "Reverse charge",
    "taxProcedure.EXPORT": "Export",
    "reverseChargeRequiresVatId": "Reverse charge requires a VAT ID",
    "taxCountry": "VAT country"
  },
  "de": {
    "taxProcedure": "Steuerverfahren",
    "taxProcedure.REGULAR": "Regulär",
    "taxProcedure.REVERSE_CHARGE": "Reverse Charge",
    "taxProcedure.EXPORT": "Export",
    "reverseChargeRequiresVatId": "Reverse Charge erfordert eine USt-IdNr.",
    "taxCountry": "Steuerland"
  },
  "es": {
    "taxCountry": "País de IVA"
  }
}
</i18n>
